import type { FC, ReactNode, RefObject } from 'react'

import { useRouter } from 'next/router'
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useScroll
} from 'motion/react'

import { cn } from '@/lib/utils'
import { dockProgress } from '@/lib/hero-dock'
import { useIntroReady } from '@/components/generals/intro'
import { TransitionLink } from '@/components/generals/page-transition'
import { useScrollLock } from '@/components/generals/smooth-scroll'
import { navigation, siteConfig } from '@/constants/site'

const CURTAIN_EASE = [0.76, 0, 0.24, 1] as const
const SOFT_EASE = [0.22, 1, 0.36, 1] as const

/** Where the giant wordmark sits at the top of the home page: the header height plus the hero's top padding. */
const HERO_TOP = 80
const HERO_TOP_WIDE = 84
const HERO_MARGIN = 16
const HERO_MARGIN_WIDE = 30

/** Share of the width between the margins the giant wordmark fills, centred. */
const HERO_SCALE = 0.82

/** The small "AUREN" logo. The home wordmark uses the same letter styles, so it shrinks into it exactly. */
const WORDMARK_CLASS = 'whitespace-nowrap font-semibold uppercase leading-[0.8] tracking-[-0.06em]'

type Geometry = {
    bigLeft: number
    bigTop: number
    bigWidth: number
    slotLeft: number
    slotTop: number
    slotWidth: number
}

interface DockingWordmarkProps {
    slotRef: RefObject<HTMLSpanElement | null>
}

/**
 * Home only. The giant "AUREN" sits fixed over the hero, exactly where the
 * hero reserves its space, so the portraits scroll underneath it. Past a
 * point it shrinks and travels into the logo slot in the top left corner.
 * The header blends with difference, so the letters invert over the photos.
 */
const DockingWordmark: FC<DockingWordmarkProps> = ({ slotRef }): ReactNode => {
    const ready = useIntroReady()
    const { scrollY } = useScroll()
    const wordRef = useRef<HTMLSpanElement>(null)
    const geometryRef = useRef<Geometry | null>(null)
    const [measured, setMeasured] = useState(false)

    const apply = useCallback((scroll: number) => {
        const word = wordRef.current
        const geometry = geometryRef.current

        if (!word || !geometry) return

        const progress = dockProgress(scroll, window.innerHeight)
        const scale = 1 + ((geometry.slotWidth / geometry.bigWidth) - 1) * progress
        const x = geometry.bigLeft + (geometry.slotLeft - geometry.bigLeft) * progress
        const y = geometry.bigTop + (geometry.slotTop - geometry.bigTop) * progress

        word.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) scale(${scale.toFixed(5)})`
    }, [])

    /**
     * Sizes the giant wordmark to fill the page width between the margins and
     * publishes its height as --hero-wordmark-h, which the hero reserves
     * above the portraits. One source of truth, so the two can never drift.
     */
    const measure = useCallback(() => {
        const slot = slotRef.current
        const word = wordRef.current

        if (!slot || !word) return

        const wide = window.matchMedia('(min-width: 768px)').matches
        const margin = wide ? HERO_MARGIN_WIDE : HERO_MARGIN
        const top = wide ? HERO_TOP_WIDE : HERO_TOP
        const pageWidth = document.documentElement.clientWidth
        const width = (pageWidth - margin * 2) * HERO_SCALE

        word.style.transform = 'none'
        word.style.fontSize = '100px'

        // Negative tracking also trails the last letter, so its edge sits 0.06em past the box.
        const em = word.getBoundingClientRect().width / 100 + 0.06

        if (em <= 0.06 || width <= 0) return

        word.style.fontSize = `${(width / em).toFixed(3)}px`

        const bigRect = word.getBoundingClientRect()
        const slotRect = slot.getBoundingClientRect()

        if (!bigRect.width || !slotRect.width) return

        document.documentElement.style.setProperty('--hero-wordmark-h', `${bigRect.height.toFixed(2)}px`)

        geometryRef.current = {
            bigLeft: (pageWidth - width) / 2,
            bigTop: top,
            bigWidth: bigRect.width,
            slotLeft: slotRect.left,
            slotTop: slotRect.top,
            slotWidth: slotRect.width
        }

        apply(window.scrollY)
        setMeasured(true)
    }, [apply, slotRef])

    useEffect(() => {
        let frame = 0
        let lastWidth = -1

        const schedule = () => {
            window.cancelAnimationFrame(frame)
            frame = window.requestAnimationFrame(measure)
        }

        // The page width also changes when the scrollbar appears after the opening sequence.
        const observer = new ResizeObserver(() => {
            const width = document.documentElement.clientWidth

            if (width === lastWidth) return

            lastWidth = width
            schedule()
        })

        observer.observe(document.documentElement)
        document.fonts?.ready.then(schedule)
        schedule()

        return () => {
            window.cancelAnimationFrame(frame)
            observer.disconnect()
        }
    }, [measure])

    useMotionValueEvent(scrollY, 'change', apply)

    return (
        <span
            ref={wordRef}
            aria-hidden='true'
            className={cn(
                WORDMARK_CLASS,
                'pointer-events-none absolute left-0 top-0 origin-top-left select-none text-[33vw] text-white transition-opacity duration-[1200ms] ease-out',
                measured && ready ? 'opacity-100' : 'opacity-0'
            )}
        >
            Auren
        </span>
    )
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const { scrollY } = useScroll()
    const isHome = router.pathname === '/'
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [docked, setDocked] = useState(!isHome)
    const [gathered, setGathered] = useState(!isHome)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)
    const slotRef = useRef<HTMLSpanElement>(null)

    const syncDock = useCallback((latest: number) => {
        if (!isHome) return

        const progress = dockProgress(latest, window.innerHeight)

        setDocked(progress > 0.98)
        setGathered(progress > 0.5)
    }, [isHome])

    useEffect(() => {
        syncDock(window.scrollY)
    }, [syncDock])

    useMotionValueEvent(scrollY, 'change', latest => {
        const previous = scrollY.getPrevious() ?? 0

        setScrolled(latest > 24)
        syncDock(latest)

        if (isHome) return

        if (latest > previous + 2 && latest > 140) setHidden(true)
        else if (latest < previous - 2) setHidden(false)
    })

    const isActive = useCallback((href: string) => {
        if (href === '/') return router.pathname === '/'

        return router.pathname.startsWith(href)
    }, [router.pathname])

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
    }, [])

    return (
        <>
            <motion.header
                layoutRoot
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    isHome && 'text-white mix-blend-difference',
                    hidden && !menuOpen && '-translate-y-full'
                )}
                onFocusCapture={() => setHidden(false)}
            >
                {!isHome && (
                    <div
                        aria-hidden='true'
                        className={cn(
                            'absolute inset-0 border-b border-white/10 bg-background/60 backdrop-blur-xl transition-opacity duration-500',
                            scrolled ? 'opacity-100' : 'opacity-0'
                        )}
                    />
                )}

                {isHome && <DockingWordmark slotRef={slotRef} />}

                <div className='relative flex h-18 items-center px-4 md:px-[30px]'>
                    <TransitionLink
                        href='/'
                        aria-label='Auren, back to the home page'
                        tabIndex={docked ? undefined : -1}
                        className={cn(
                            'absolute left-4 top-1/2 z-10 -translate-y-1/2 py-2 transition-colors md:left-[30px]',
                            isHome ? 'text-transparent' : 'text-foreground hover:text-gold',
                            !docked && 'invisible'
                        )}
                    >
                        <span ref={slotRef} className={cn(WORDMARK_CLASS, 'block text-[24px] md:text-[28px]')}>
                            Auren
                        </span>
                    </TransitionLink>

                    <nav aria-label='Main' className='hidden flex-1 md:flex'>
                        <ul
                            className={cn(
                                'flex items-center text-[13px] uppercase tracking-[0.04em]',
                                gathered ? 'ml-auto gap-10' : 'w-full justify-between'
                            )}
                        >
                            {navigation.map(item => (
                                <motion.li
                                    key={item.href}
                                    layout='position'
                                    transition={{ duration: 0.8, ease: SOFT_EASE }}
                                >
                                    <TransitionLink
                                        href={item.href}
                                        aria-current={isActive(item.href) ? 'page' : undefined}
                                        className={cn(
                                            'group relative inline-flex py-2 transition-colors duration-300',
                                            isHome
                                                ? 'text-white/80 hover:text-white aria-[current=page]:text-white'
                                                : 'text-muted-foreground hover:text-foreground aria-[current=page]:text-foreground'
                                        )}
                                    >
                                        {item.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-0 bottom-1 h-px origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100 group-aria-[current=page]:scale-x-100',
                                                isHome ? 'bg-white' : 'bg-gold'
                                            )}
                                        />
                                    </TransitionLink>
                                </motion.li>
                            ))}
                        </ul>
                    </nav>

                    <button
                        ref={menuButtonRef}
                        type='button'
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        onClick={() => setMenuOpen(true)}
                        className='ml-auto flex size-11 flex-col items-center justify-center gap-1.5 rounded-full md:hidden'
                    >
                        <span className='sr-only'>
                            Open menu
                        </span>

                        <span aria-hidden='true' className={cn('block h-px w-6', isHome ? 'bg-white' : 'bg-foreground')} />
                        <span aria-hidden='true' className={cn('block h-px w-4 self-end', isHome ? 'bg-white' : 'bg-foreground')} />
                    </button>
                </div>
            </motion.header>

            <MobileMenu open={menuOpen} onClose={closeMenu} isActive={isActive} />
        </>
    )
}

interface MobileMenuProps {
    open: boolean
    onClose: () => void
    isActive: (href: string) => boolean
}

const MobileMenu: FC<MobileMenuProps> = ({ open, onClose, isActive }): ReactNode => {
    const panelRef = useRef<HTMLDivElement>(null)

    useScrollLock(open)

    useEffect(() => {
        if (!open) return

        const focusable = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [])
        const timer = window.setTimeout(() => focusable()[0]?.focus(), 80)

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                onClose()
                return
            }

            if (event.key !== 'Tab') return

            const items = focusable()
            const first = items[0]
            const last = items[items.length - 1]

            if (!first || !last) return

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener('keydown', handleKey)

        return () => {
            window.clearTimeout(timer)
            document.removeEventListener('keydown', handleKey)
        }
    }, [open, onClose])

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    ref={panelRef}
                    id='mobile-menu'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Site menu'
                    className='fixed inset-0 z-[60] flex flex-col bg-background md:hidden'
                    initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                    animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                    exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                    transition={{ duration: 0.75, ease: CURTAIN_EASE }}
                >
                    <div className='shell flex h-18 items-center justify-between'>
                        <span className={cn(WORDMARK_CLASS, 'text-[24px]')}>
                            Auren
                        </span>

                        <button
                            type='button'
                            onClick={onClose}
                            className='flex size-11 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-gold'
                        >
                            <X className='size-5' aria-hidden='true' />

                            <span className='sr-only'>
                                Close menu
                            </span>
                        </button>
                    </div>

                    <nav aria-label='Mobile' className='shell flex flex-1 flex-col justify-center'>
                        <ul className='space-y-2'>
                            {navigation.map((item, index) => (
                                <li key={item.href} className='overflow-hidden pb-1'>
                                    <motion.div
                                        initial={{ y: '115%' }}
                                        animate={{ y: '0%' }}
                                        exit={{ y: '115%', transition: { duration: 0.35, ease: SOFT_EASE } }}
                                        transition={{ duration: 0.9, delay: 0.3 + index * 0.08, ease: SOFT_EASE }}
                                    >
                                        <TransitionLink
                                            href={item.href}
                                            onClick={onClose}
                                            aria-current={isActive(item.href) ? 'page' : undefined}
                                            className='flex items-baseline gap-4 py-1 text-[clamp(2.75rem,13vw,4.5rem)] font-light leading-none tracking-[-0.05em] text-foreground transition-colors aria-[current=page]:text-gold'
                                        >
                                            <span className='text-xs tracking-normal text-muted-foreground'>
                                                {`0${index + 1}`}
                                            </span>

                                            {item.label}
                                        </TransitionLink>
                                    </motion.div>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <motion.div
                        className='shell space-y-6 pb-10'
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0, transition: { delay: 0.65, duration: 0.6 } }}
                        exit={{ opacity: 0, transition: { duration: 0.2 } }}
                    >
                        <div className='flex items-center gap-3 text-sm text-muted-foreground'>
                            <span className='pulse-dot' aria-hidden='true' />
                            Open for new projects
                        </div>

                        <div className='flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-6 text-sm'>
                            <a href={`mailto:${siteConfig.email}`} className='text-foreground transition-colors hover:text-gold'>
                                {siteConfig.email}
                            </a>

                            <ul className='flex gap-5 text-muted-foreground'>
                                {siteConfig.socials.map(social => (
                                    <li key={social.label}>
                                        <a href={social.href} target='_blank' rel='noopener noreferrer' className='transition-colors hover:text-gold'>
                                            {social.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
